import React from 'react';

import {assertAndContinue} from 'Asserts';
import {isChemSearch} from 'chemistry/ChemSearchUtils';
import ListingSettingsDropdown, {SortItem} from 'nav/ListingSettingsDropdown';
import {isBLASTSearch} from 'search/blast/BLASTUtil';
import {SearchParams, Sort, SortKey} from 'search/SearchConstants';
import {isQuerySearch} from 'search/SearchUtil';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';

import {AVAILABLE_PAGE_SIZES} from '../PageSizeUtil';

function getItemsForSection(sortKey: string): Array<Omit<SortItem, 'sortKey' | 'sortKeyDisplayName'>> {
  switch (sortKey) {
    case SortKey.SORT_NAME_KEY: {
      return [
        {
          reverse: false,
          reverseDisplayName: 'Alphabetical',
        },
        {
          reverse: true,
          reverseDisplayName: 'Reverse alphabetical',
        },
      ];
    }

    case SortKey.SORT_MODIFIED_AT_KEY: {
      return [
        {
          reverse: true,
          reverseDisplayName: 'Newest',
        },
        {
          reverse: false,
          reverseDisplayName: 'Oldest',
        },
      ];
    }

    case SortKey.SORT_IDENTIFIER_KEY: {
      return [
        {
          reverse: false,
          reverseDisplayName: 'Alphabetical',
        },
        {
          reverse: true,
          reverseDisplayName: 'Reverse alphabetical',
        },
      ];
    }

    case SortKey.SORT_CREATED_AT_KEY: {
      return [
        {
          reverse: true,
          reverseDisplayName: 'Newest',
        },
        {
          reverse: false,
          reverseDisplayName: 'Oldest',
        },
      ];
    }

    case SortKey.SORT_SCHEDULED_ON_KEY: {
      return [
        {
          reverse: true,
          reverseDisplayName: 'Newest',
        },
        {
          reverse: false,
          reverseDisplayName: 'Oldest',
        },
      ];
    }

    case SortKey.SORT_BARCODE_KEY: {
      return [
        {
          reverse: false,
          reverseDisplayName: 'Alphabetical',
        },
        {
          reverse: true,
          reverseDisplayName: 'Reverse alphabetical',
        },
      ];
    }

    case SortKey.SORT_REGISTRY_IDENTIFIER_KEY: {
      return [
        {
          reverse: false,
          reverseDisplayName: 'Alphabetical',
        },
        {
          reverse: true,
          reverseDisplayName: 'Reverse alphabetical',
        },
      ];
    }

    case SortKey.SORT_CHEMICAL_SIMILARITY: {
      return [
        {
          reverse: false,
          reverseDisplayName: 'Descending',
        },
        {
          reverse: true,
          reverseDisplayName: 'Ascending',
        },
      ];
    }

    case SortKey.SORT_BLAST_E_VALUE: {
      return [
        {
          reverse: false,
          reverseDisplayName: 'Descending',
        },
        {
          reverse: true,
          reverseDisplayName: 'Ascending',
        },
      ];
    }

    case SortKey.SORT_REQUEST_V2_DEFINITION_NAME: {
      return [
        {
          reverse: false,
          reverseDisplayName: 'Alphabetical',
        },
        {
          reverse: true,
          reverseDisplayName: 'Reverse alphabetical',
        },
      ];
    }

    default: {
      assertAndContinue(false, `Unsupported sortKey: ${sortKey}`);
      return [];
    }
  }
}

export function getSortItemSections(availableSortKeys: Array<string>): Array<Array<SortItem>> {
  return availableSortKeys.map((sortKey) =>
    getItemsForSection(sortKey).map((item) => {
      const sortKeyDisplayName = getColumnHeaderFromSortKey(sortKey as SortKey);
      return {...item, sortKey, sortKeyDisplayName};
    })
  );
}

export function getSortItemSectionsForSearchParams(
  searchParams: SearchParams,
  availableSortKeys: Array<string>
): Array<Array<SortItem>> {
  const sortOptions = getSortItemSections(availableSortKeys);
  if (isChemSearch(searchParams)) {
    return [...getSortItemSections([SortKey.SORT_CHEMICAL_SIMILARITY]), ...sortOptions];
  } else if (isBLASTSearch(searchParams)) {
    return [...getSortItemSections([SortKey.SORT_BLAST_E_VALUE]), ...sortOptions];
  } else if (!isQuerySearch(searchParams)) {
    return sortOptions;
  } else {
    return [
      [
        {
          sortKey: SortKey.SORT_RELEVANCE_KEY,
          reverse: false,
          sortKeyDisplayName: getColumnHeaderFromSortKey(SortKey.SORT_RELEVANCE_KEY),
          reverseDisplayName: null,
        },
      ],
      ...sortOptions,
    ];
  }
}

export interface SidepanelListingSettingsDropdownProps {
  onSetPageSize: (limit: number) => void;
  onSetSortOrder: (sorts: Array<Sort>) => void;
  searchParams: SearchParams;
  availableSortKeys: Array<string>;
  showSortSection?: boolean;
  displaySettingsConfig: {
    isCompact: boolean;
    setIsCompactDisplay: (isCompact: boolean) => void;
  };
}

export default function SidepanelListingSettingsDropdown(
  props: SidepanelListingSettingsDropdownProps
): JSX.Element {
  const showSortSection = props.showSortSection ?? true;
  const sortConfig = {
    availableSortOptions: getSortItemSectionsForSearchParams(props.searchParams, props.availableSortKeys),
    sorts: props.searchParams.sorts,
    onSetSort: (sortKey: string, reverse: boolean) => {
      props.onSetSortOrder([{key: sortKey, reverse}]);
    },
  };
  return (
    <ListingSettingsDropdown
      pageSizeConfig={{
        availablePageSizes: AVAILABLE_PAGE_SIZES,
        pageSize: props.searchParams.limit,
        onSetPageSize: props.onSetPageSize,
      }}
      displaySettingsConfig={props.displaySettingsConfig}
      sortConfig={showSortSection ? sortConfig : undefined}
    />
  );
}
